import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Tag, Progress, Input, Select, Empty, Spin,
  Dropdown, Modal, message, Button, Statistic, Pagination, Tooltip,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRocket, faPaperPlane, faEnvelopeOpenText, faReply, faBullhorn,
  faPlus, faSearch, faEllipsisVertical, faPlay, faPause, faStop,
  faTrash, faEye, faLayerGroup, faBook, faArrowsRotate, faCircle,
  faCalendarDay, faUser, faChartLine,
} from '@fortawesome/free-solid-svg-icons';
import {
  fetchCampaigns,
  changeCampaignStatus,
  deleteCampaign,
  fetchDomainHealth,
} from '../../../Slices/bulkOutreachSlice';
import { setLoader } from '../../../Slices/loaderSlice';
import DomainHealthPanel from './components/DomainHealthPanel';

/* ═══════════════════════════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════════════════════════ */

const COLORS = {
  primary: '#1a1a2e',
  primaryLight: '#16213e',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  accent: '#8b5cf6',
  info: '#3b82f6',
  textPrimary: '#1e293b',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  border: '#e2e8f0',
  bgPage: '#f8fafc',
  white: '#ffffff',
};

const STATUS_CONFIG = {
  draft:     { color: COLORS.accent,  bg: '#8b5cf610', label: 'Draft' },
  active:    { color: COLORS.success, bg: '#10b98110', label: 'Active' },
  paused:    { color: COLORS.warning, bg: '#f59e0b10', label: 'Paused' },
  completed: { color: COLORS.info,    bg: '#3b82f610', label: 'Completed' },
  cancelled: { color: COLORS.error,   bg: '#ef444410', label: 'Cancelled' },
};

const STATUS_OPTIONS = [
  { label: 'Draft',     value: 'draft' },
  { label: 'Active',    value: 'active' },
  { label: 'Paused',    value: 'paused' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' },
];

const PAGE_SIZE = 12;

/* ═══════════════════════════════════════════════════════════════════════════
   STYLES
   ═══════════════════════════════════════════════════════════════════════════ */

const styles = {
  pageWrapper: {
    minHeight: '100vh',
    background: COLORS.bgPage,
    padding: '24px 32px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 28,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: COLORS.primary,
    margin: 0,
    letterSpacing: '-0.02em',
  },
  pageSubtitle: {
    fontSize: 14,
    color: COLORS.textSecondary,
    margin: '4px 0 0 0',
    fontWeight: 400,
  },
  headerActions: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },

  /* KPI Cards */
  kpiCard: {
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'linear-gradient(135deg, #667eea11 0%, #764ba211 100%)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
    padding: '20px 24px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'default',
    height: '100%',
  },
  kpiIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  kpiLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 4,
  },
  kpiValue: {
    fontSize: 28,
    fontWeight: 700,
    color: COLORS.textPrimary,
    lineHeight: 1.1,
  },
  kpiSuffix: {
    fontSize: 14,
    fontWeight: 500,
    color: COLORS.textMuted,
    marginLeft: 2,
  },

  /* Filter Bar */
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  searchInput: {
    width: 280,
    borderRadius: 10,
    height: 40,
  },
  statusSelect: {
    minWidth: 220,
    borderRadius: 10,
  },
  actionBtn: {
    borderRadius: 10,
    height: 40,
    fontWeight: 600,
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  primaryBtn: {
    background: `linear-gradient(135deg, ${COLORS.accent} 0%, #7c3aed 100%)`,
    border: 'none',
    color: COLORS.white,
    borderRadius: 10,
    height: 40,
    fontWeight: 600,
    fontSize: 13,
    paddingLeft: 18,
    paddingRight: 18,
    boxShadow: '0 2px 8px rgba(139,92,246,0.3)',
  },
  secondaryBtn: {
    background: COLORS.white,
    border: `1px solid ${COLORS.border}`,
    color: COLORS.textPrimary,
    borderRadius: 10,
    height: 40,
    fontWeight: 600,
    fontSize: 13,
    paddingLeft: 16,
    paddingRight: 16,
  },

  /* Campaign Card */
  campaignCard: {
    borderRadius: 16,
    border: `1px solid ${COLORS.border}`,
    background: COLORS.white,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    padding: 0,
    overflow: 'hidden',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'pointer',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  campaignCardHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  },
  cardBody: {
    padding: '20px 22px 16px 22px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 14,
  },
  campaignName: {
    fontSize: 16,
    fontWeight: 650,
    color: COLORS.textPrimary,
    margin: 0,
    lineHeight: 1.3,
    maxWidth: '80%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusTag: {
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: '2px 10px',
    border: 'none',
    lineHeight: '20px',
  },
  recipientRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    color: COLORS.textSecondary,
    marginBottom: 14,
  },
  progressSection: {
    marginBottom: 16,
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 12,
    color: COLORS.textMuted,
    marginBottom: 6,
    fontWeight: 500,
  },
  metricsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 14,
  },
  metricBox: {
    flex: 1,
    textAlign: 'center',
    padding: '10px 6px',
    borderRadius: 10,
    background: '#f8fafc',
  },
  metricValue: {
    fontSize: 16,
    fontWeight: 700,
    color: COLORS.textPrimary,
    lineHeight: 1.2,
  },
  metricLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginTop: 2,
  },
  cardFooter: {
    borderTop: `1px solid ${COLORS.border}`,
    padding: '12px 22px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 12,
    color: COLORS.textMuted,
    background: '#fafbfc',
  },
  footerItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  moreBtn: {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: 'none',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: COLORS.textMuted,
    fontSize: 14,
    transition: 'background 0.15s',
  },

  /* Pagination */
  paginationWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 28,
    paddingBottom: 20,
  },

  /* Empty / Loading */
  emptyWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 340,
    background: COLORS.white,
    borderRadius: 16,
    border: `1px solid ${COLORS.border}`,
  },
  spinWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 340,
  },

  /* Domain Health Strip */
  domainHealthStrip: {
    marginBottom: 24,
  },

  refreshBtn: {
    background: 'transparent',
    border: `1px solid ${COLORS.border}`,
    color: COLORS.textSecondary,
    borderRadius: 10,
    height: 40,
    width: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

/* ═══════════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

const formatPercent = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  return Number(value).toFixed(1);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '--';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '--';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const getStepCompletion = (campaign) => {
  if (!campaign) return 0;
  const steps = campaign.steps || campaign.sequence || [];
  if (!steps.length) return 0;
  const completedSteps = steps.filter(s => s.status === 'completed' || s.sent).length;
  return Math.round((completedSteps / steps.length) * 100);
};

const getTotalSteps = (campaign) => {
  if (!campaign) return 0;
  return (campaign.steps || campaign.sequence || []).length;
};

const getCompletedSteps = (campaign) => {
  if (!campaign) return 0;
  const steps = campaign.steps || campaign.sequence || [];
  return steps.filter(s => s.status === 'completed' || s.sent).length;
};

/* ═══════════════════════════════════════════════════════════════════════════
   KPI CARD COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const KpiCard = ({ icon, iconBg, label, value, suffix, trend }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        ...styles.kpiCard,
        ...(hovered ? { transform: 'translateY(-3px)', boxShadow: '0 8px 32px rgba(0,0,0,0.10)' } : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ ...styles.kpiIconWrap, background: iconBg }}>
        <FontAwesomeIcon icon={icon} style={{ fontSize: 18, color: iconBg.replace('18', '').replace('#', '#') }} />
      </div>
      <div style={styles.kpiLabel}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={styles.kpiValue}>{value}</span>
        {suffix && <span style={styles.kpiSuffix}>{suffix}</span>}
      </div>
      {trend !== undefined && trend !== null && (
        <div style={{
          fontSize: 12,
          fontWeight: 600,
          marginTop: 6,
          color: trend >= 0 ? COLORS.success : COLORS.error,
        }}>
          {trend >= 0 ? '+' : ''}{trend}% vs last period
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   CAMPAIGN CARD COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const CampaignCard = ({ campaign, onAction, onViewDetails }) => {
  const [hovered, setHovered] = useState(false);

  const statusConf = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
  const totalRecipients = campaign.recipientCount || campaign.totalRecipients || 0;
  const sentCount = campaign.sentCount || campaign.metrics?.sent || 0;
  const openRate = campaign.openRate || campaign.metrics?.openRate || 0;
  const replyRate = campaign.replyRate || campaign.metrics?.replyRate || 0;
  const stepPercent = getStepCompletion(campaign);
  const totalSteps = getTotalSteps(campaign);
  const completedSteps = getCompletedSteps(campaign);

  const progressColor = campaign.status === 'completed'
    ? COLORS.success
    : campaign.status === 'active'
      ? COLORS.info
      : COLORS.accent;

  const menuItems = [];

  if (campaign.status === 'draft') {
    menuItems.push({
      key: 'start',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FontAwesomeIcon icon={faPlay} style={{ color: COLORS.success, width: 12 }} /> Start Campaign
        </span>
      ),
    });
  }
  if (campaign.status === 'active') {
    menuItems.push({
      key: 'pause',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FontAwesomeIcon icon={faPause} style={{ color: COLORS.warning, width: 12 }} /> Pause
        </span>
      ),
    });
  }
  if (campaign.status === 'paused') {
    menuItems.push({
      key: 'resume',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FontAwesomeIcon icon={faPlay} style={{ color: COLORS.success, width: 12 }} /> Resume
        </span>
      ),
    });
  }
  if (campaign.status === 'active' || campaign.status === 'paused') {
    menuItems.push({
      key: 'cancel',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FontAwesomeIcon icon={faStop} style={{ color: COLORS.error, width: 12 }} /> Cancel
        </span>
      ),
    });
  }
  menuItems.push({ type: 'divider' });
  menuItems.push({
    key: 'view',
    label: (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FontAwesomeIcon icon={faEye} style={{ color: COLORS.info, width: 12 }} /> View Details
      </span>
    ),
  });
  if (campaign.status === 'draft' || campaign.status === 'cancelled' || campaign.status === 'completed') {
    menuItems.push({
      key: 'delete',
      label: (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FontAwesomeIcon icon={faTrash} style={{ color: COLORS.error, width: 12 }} /> Delete
        </span>
      ),
      danger: true,
    });
  }

  const handleMenuClick = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    if (key === 'view') {
      onViewDetails(campaign._id, campaign.status);
    } else {
      onAction(key, campaign);
    }
  };

  return (
    <div
      style={{
        ...styles.campaignCard,
        ...(hovered ? styles.campaignCardHover : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onViewDetails(campaign._id, campaign.status)}
    >
      {/* Active indicator strip */}
      {campaign.status === 'active' && (
        <div style={{
          height: 3,
          background: `linear-gradient(90deg, ${COLORS.success} 0%, #34d399 100%)`,
        }} />
      )}

      <div style={styles.cardBody}>
        {/* Header: Name + Status */}
        <div style={styles.cardHeader}>
          <Tooltip title={campaign.name} placement="topLeft">
            <h4 style={styles.campaignName}>{campaign.name || 'Untitled Campaign'}</h4>
          </Tooltip>
          <Tag
            style={{
              ...styles.statusTag,
              color: statusConf.color,
              background: statusConf.bg,
            }}
          >
            {campaign.status === 'active' && (
              <FontAwesomeIcon
                icon={faCircle}
                style={{ fontSize: 6, marginRight: 5, animation: 'pulse 1.5s infinite' }}
              />
            )}
            {statusConf.label}
          </Tag>
        </div>

        {/* Recipient count */}
        <div style={styles.recipientRow}>
          <FontAwesomeIcon icon={faUser} style={{ fontSize: 11, color: COLORS.textMuted }} />
          <span>{formatNumber(totalRecipients)} recipients</span>
        </div>

        {/* Progress bar */}
        {totalSteps > 0 && (
          <div style={styles.progressSection}>
            <div style={styles.progressLabel}>
              <span>Step Progress</span>
              <span>{completedSteps} / {totalSteps} steps</span>
            </div>
            <Progress
              percent={stepPercent}
              strokeColor={progressColor}
              trailColor="#e2e8f0"
              size="small"
              showInfo={false}
              style={{ margin: 0 }}
            />
          </div>
        )}

        {/* Metrics */}
        <div style={styles.metricsRow}>
          <div style={styles.metricBox}>
            <div style={styles.metricValue}>{formatNumber(sentCount)}</div>
            <div style={styles.metricLabel}>Sent</div>
          </div>
          <div style={{ ...styles.metricBox, background: '#f0fdf4' }}>
            <div style={{ ...styles.metricValue, color: COLORS.success }}>{formatPercent(openRate)}%</div>
            <div style={styles.metricLabel}>Opened</div>
          </div>
          <div style={{ ...styles.metricBox, background: '#eff6ff' }}>
            <div style={{ ...styles.metricValue, color: COLORS.info }}>{formatPercent(replyRate)}%</div>
            <div style={styles.metricLabel}>Replied</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={styles.cardFooter}>
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={styles.footerItem}>
            <FontAwesomeIcon icon={faCalendarDay} style={{ fontSize: 11 }} />
            <span>{formatDate(campaign.startedAt || campaign.createdAt)}</span>
          </div>
          {campaign.createdByName && (
            <div style={styles.footerItem}>
              <FontAwesomeIcon icon={faUser} style={{ fontSize: 11 }} />
              <span>{campaign.createdByName}</span>
            </div>
          )}
        </div>
        <Dropdown
          menu={{ items: menuItems, onClick: handleMenuClick }}
          trigger={['click']}
          placement="bottomRight"
        >
          <button
            style={styles.moreBtn}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </button>
        </Dropdown>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN DASHBOARD COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

const CampaignDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    campaigns,
    campaignsTotal,
    campaignStats,
    domainHealth,
    loading,
  } = useSelector((state) => state.bulkOutreach);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  /* ── Data fetching ──────────────────────────────────────────────────────── */

  const loadCampaigns = useCallback(() => {
    const params = { page: currentPage, limit: PAGE_SIZE };
    if (statusFilter.length === 1) params.status = statusFilter[0];
    if (searchTerm.trim()) params.search = searchTerm.trim();
    dispatch(fetchCampaigns(params));
  }, [dispatch, currentPage, statusFilter, searchTerm]);

  useEffect(() => {
    loadCampaigns();
    const interval = setInterval(loadCampaigns, 30000);
    return () => clearInterval(interval);
  }, [loadCampaigns]);

  useEffect(() => {
    dispatch(fetchDomainHealth(7));
  }, [dispatch]);

  useEffect(() => {
    dispatch(setLoader(loading));
  }, [loading, dispatch]);

  /* ── Handlers ───────────────────────────────────────────────────────────── */

  const handleRefresh = useCallback(async () => {
    if (refreshing) return; // prevent double-click
    setRefreshing(true);
    try {
      await Promise.all([
        dispatch(fetchCampaigns({ page: currentPage, limit: PAGE_SIZE })).unwrap(),
        dispatch(fetchDomainHealth(7)).unwrap(),
      ]);
      message.success('Dashboard refreshed');
    } catch (err) {
      message.error(typeof err === 'string' ? err : 'Failed to refresh data — check if the backend is running');
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, currentPage, refreshing]);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((values) => {
    setStatusFilter(values);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleAction = useCallback(async (action, campaign) => {
    const statusMap = {
      start: 'active',
      pause: 'paused',
      resume: 'active',
      cancel: 'cancelled',
    };

    if (action === 'delete') {
      setCampaignToDelete(campaign);
      setDeleteModalVisible(true);
      return;
    }

    const newStatus = statusMap[action];
    if (!newStatus) return;

    const actionLabels = { start: 'Starting', pause: 'Pausing', resume: 'Resuming', cancel: 'Cancelling' };
    const label = actionLabels[action] || action;

    try {
      await dispatch(changeCampaignStatus({ id: campaign._id, status: newStatus })).unwrap();
      message.success(`${label} campaign: ${campaign.name}`);
      loadCampaigns();
    } catch (err) {
      message.error(typeof err === 'string' ? err : `Failed to ${action} campaign`);
    }
  }, [dispatch, loadCampaigns]);

  const handleConfirmDelete = useCallback(async () => {
    if (!campaignToDelete) return;
    try {
      await dispatch(deleteCampaign(campaignToDelete._id)).unwrap();
      message.success(`Deleted campaign: ${campaignToDelete.name}`);
      setDeleteModalVisible(false);
      setCampaignToDelete(null);
      loadCampaigns();
    } catch (err) {
      message.error(typeof err === 'string' ? err : 'Failed to delete campaign');
    }
  }, [dispatch, campaignToDelete, loadCampaigns]);

  const handleViewDetails = useCallback((id, status) => {
    if (status === 'draft') {
      navigate(`/bulk-outreach/new?edit=${id}`);
    } else {
      navigate(`/bulk-outreach/${id}`);
    }
  }, [navigate]);

  /* ── Filtered campaigns (client-side filter for multi-status) ──────────── */

  const filteredCampaigns = useMemo(() => {
    if (statusFilter.length <= 1) return campaigns;
    return campaigns.filter(c => statusFilter.includes(c.status));
  }, [campaigns, statusFilter]);

  /* ── KPI values ─────────────────────────────────────────────────────────── */

  const stats = campaignStats || {};
  const totalCampaigns = stats.total || campaignsTotal || 0;
  const activeCampaigns = stats.active || campaigns.filter(c => c.status === 'active').length;
  const totalSent = stats.totalSent || stats.emailsSent || 0;
  const avgOpenRate = stats.avgOpenRate || stats.openRate || 0;
  const avgReplyRate = stats.avgReplyRate || stats.replyRate || 0;

  /* ── Render ─────────────────────────────────────────────────────────────── */

  return (
    <div style={styles.pageWrapper}>
      {/* Pulse animation for active status dot */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>

      {/* ── Page Header ────────────────────────────────────────────────── */}
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>
            <FontAwesomeIcon icon={faBullhorn} style={{ marginRight: 12, color: COLORS.accent }} />
            Bulk Outreach
          </h1>
          <p style={styles.pageSubtitle}>
            Manage campaigns, track engagement, and optimize deliverability
          </p>
        </div>
        <div style={styles.headerActions}>
          <Tooltip title="Refresh dashboard">
            <button
              style={{ ...styles.refreshBtn, opacity: refreshing ? 0.5 : 1, cursor: refreshing ? 'not-allowed' : 'pointer' }}
              onClick={handleRefresh}
              disabled={refreshing}
              onMouseEnter={(e) => { if (!refreshing) { e.currentTarget.style.borderColor = COLORS.accent; e.currentTarget.style.color = COLORS.accent; } }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.textSecondary; }}
            >
              <FontAwesomeIcon icon={faArrowsRotate} spin={refreshing} />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* ── KPI Cards ──────────────────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={4} xl={4}>
          <KpiCard
            icon={faBullhorn}
            iconBg="#8b5cf618"
            label="Total Campaigns"
            value={formatNumber(totalCampaigns)}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4} xl={4}>
          <KpiCard
            icon={faRocket}
            iconBg="#10b98118"
            label="Active"
            value={formatNumber(activeCampaigns)}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4} xl={4}>
          <KpiCard
            icon={faPaperPlane}
            iconBg="#3b82f618"
            label="Emails Sent"
            value={formatNumber(totalSent)}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <KpiCard
            icon={faEnvelopeOpenText}
            iconBg="#f59e0b18"
            label="Open Rate"
            value={formatPercent(avgOpenRate)}
            suffix="%"
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={6}>
          <KpiCard
            icon={faReply}
            iconBg="#10b98118"
            label="Reply Rate"
            value={formatPercent(avgReplyRate)}
            suffix="%"
          />
        </Col>
      </Row>

      {/* ── Domain Health Strip ─────────────────────────────────────────── */}
      <div style={styles.domainHealthStrip}>
        <DomainHealthPanel domains={domainHealth} compact />
      </div>

      {/* ── Search + Filter Bar ────────────────────────────────────────── */}
      <div style={styles.filterBar}>
        <Input
          placeholder="Search campaigns..."
          prefix={<FontAwesomeIcon icon={faSearch} style={{ color: COLORS.textMuted }} />}
          value={searchTerm}
          onChange={handleSearchChange}
          style={styles.searchInput}
          allowClear
        />
        <Select
          mode="multiple"
          placeholder="Filter by status"
          value={statusFilter}
          onChange={handleStatusFilterChange}
          options={STATUS_OPTIONS}
          style={styles.statusSelect}
          allowClear
          maxTagCount={2}
          maxTagTextLength={8}
        />
        <div style={{ flex: 1 }} />
        <Button
          style={styles.secondaryBtn}
          onClick={() => navigate('/bulk-outreach/segments')}
        >
          <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: 6 }} />
          Segments
        </Button>
        <Button
          style={styles.secondaryBtn}
          onClick={() => navigate('/bulk-outreach/playbooks')}
        >
          <FontAwesomeIcon icon={faBook} style={{ marginRight: 6 }} />
          Playbooks
        </Button>
        <Button
          type="primary"
          style={styles.primaryBtn}
          onClick={() => navigate('/bulk-outreach/new')}
        >
          <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
          New Campaign
        </Button>
      </div>

      {/* ── Campaign Cards Grid ────────────────────────────────────────── */}
      {loading && !campaigns.length ? (
        <div style={styles.spinWrap}>
          <Spin size="large" tip="Loading campaigns..." />
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div style={styles.emptyWrap}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              searchTerm || statusFilter.length
                ? 'No campaigns match your filters'
                : 'No campaigns yet. Create your first campaign to get started.'
            }
          >
            {!searchTerm && !statusFilter.length && (
              <Button
                type="primary"
                style={styles.primaryBtn}
                onClick={() => navigate('/bulk-outreach/new')}
              >
                <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
                Create Campaign
              </Button>
            )}
          </Empty>
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {filteredCampaigns.map((campaign) => (
              <Col key={campaign._id} xs={24} sm={12} md={8} lg={8} xl={6}>
                <CampaignCard
                  campaign={campaign}
                  onAction={handleAction}
                  onViewDetails={handleViewDetails}
                />
              </Col>
            ))}
          </Row>

          {/* ── Pagination ───────────────────────────────────────────── */}
          {campaignsTotal > PAGE_SIZE && (
            <div style={styles.paginationWrap}>
              <Pagination
                current={currentPage}
                total={campaignsTotal}
                pageSize={PAGE_SIZE}
                onChange={handlePageChange}
                showSizeChanger={false}
                showTotal={(total, range) => (
                  <span style={{ fontSize: 13, color: COLORS.textSecondary }}>
                    {range[0]}-{range[1]} of {total} campaigns
                  </span>
                )}
              />
            </div>
          )}
        </>
      )}

      {/* ── Delete Confirmation Modal ──────────────────────────────────── */}
      <Modal
        title={
          <span style={{ fontWeight: 600, fontSize: 16, color: COLORS.textPrimary }}>
            Delete Campaign
          </span>
        }
        open={deleteModalVisible}
        onCancel={() => { setDeleteModalVisible(false); setCampaignToDelete(null); }}
        onOk={handleConfirmDelete}
        okText="Delete"
        okButtonProps={{
          danger: true,
          style: { borderRadius: 8, fontWeight: 600 },
        }}
        cancelButtonProps={{
          style: { borderRadius: 8 },
        }}
        centered
        destroyOnClose
      >
        <p style={{ fontSize: 14, color: COLORS.textSecondary, margin: '16px 0' }}>
          Are you sure you want to delete <strong style={{ color: COLORS.textPrimary }}>
          {campaignToDelete?.name}</strong>? This action cannot be undone.
          All campaign data, recipients, and analytics will be permanently removed.
        </p>
      </Modal>
    </div>
  );
};

export default CampaignDashboard;
